import React, { Component } from "react";
import { getActiveBranches } from "../../Endpoint/Admin/index";
import { C, Chip } from "../Console/ConsoleUI";
import {
  isBranchScopedAdmin,
  getUserBranchId,
} from "../../services/PermissionService";

/**
 * Reusable branch-availability + per-branch price-override editor for catalog items.
 *
 * Props:
 *  - branchIds: string[]            branches the item is pinned to ([] = every branch)
 *  - excludedBranchIds: string[]    branches that do NOT carry it, even when it is at every branch
 *  - overrides: [{branchId, ...}]   per-branch price overrides (priced models only)
 *  - priceFields: [{key, label}]    price keys this model overrides (omit/[] = no price editor)
 *  - onChange: ({branchIds, excludedBranchIds, overrides}) => void
 *
 * An item reaches a branch through TWO lists, and an exclusion beats an
 * inclusion: the branch pick-list (Branches -> catalog) unticks an item by
 * adding the branch to excludedBranchIds. Editing branchIds alone therefore
 * could not put an item back on a branch that had unticked it, and the form
 * claimed "every branch" for items some branches had dropped. So this editor
 * works on what a branch actually carries and writes both lists together:
 *
 *  - "All branches" mode: branchIds = [], unticked branches go to
 *    excludedBranchIds. Branches created later carry the item too.
 *  - "Only the branches I pick" mode: branchIds = the ticked branches and
 *    excludedBranchIds = [] (so no stale exclusion can hide a picked branch).
 *
 * Every page must send excludedBranchIds alongside branchIds on save.
 *
 * Styling note: this renders inside console dialogs, whose CSS uppercases every
 * <label> to make field captions. Branch NAMES must not be shouted, so the rows
 * are built from spans and divs and only the section caption is a real caption.
 * The selection control is a plain checkbox rather than Bootstrap's
 * custom-control, which drew a blue tick that belonged to the old template.
 */
class BranchAssignment extends Component {
  constructor(props) {
    super(props);
    // priceOpen: branches whose price inputs were opened by hand. A branch that
    // already has an override shows its inputs regardless.
    this.state = { branches: [], loaded: false, priceOpen: {}, notice: "" };
  }

  componentDidMount() {
    getActiveBranches()
      .then((res) => {
        if (res && res.status === 200) {
          this.setState({ branches: res.data || [], loaded: true });
        } else {
          this.setState({ loaded: true });
        }
      })
      .catch(() => this.setState({ loaded: true }));
  }

  // ---------------------------------------------------------------- model

  ids = (list) => (list || []).map((x) => String(x && x._id ? x._id : x));

  pinned = () => this.ids(this.props.branchIds);

  excluded = () => this.ids(this.props.excludedBranchIds);

  isAllMode = () => this.pinned().length === 0;

  /** Whether a branch actually carries the item, the way the storefront decides it. */
  carries = (branchId) => {
    const id = String(branchId);
    if (this.excluded().includes(id)) return false;
    return this.isAllMode() || this.pinned().includes(id);
  };

  carriedIds = () => this.state.branches.map((b) => String(b._id)).filter(this.carries);

  emit = (branchIds, excludedBranchIds, overrides) => {
    if (this.props.onChange) {
      this.props.onChange({
        branchIds,
        excludedBranchIds,
        overrides: overrides !== undefined ? overrides : this.props.overrides || [],
      });
    }
  };

  dropOverride = (branchId) =>
    (this.props.overrides || []).filter((o) => String(o.branchId) !== String(branchId));

  // ---------------------------------------------------------------- actions

  setMode = (all) => {
    if (all === this.isAllMode()) return;
    const carried = this.carriedIds();
    const allIds = this.state.branches.map((b) => String(b._id));
    if (all) {
      // Keep exactly the branches that carry it now; future branches get it too.
      this.emit([], allIds.filter((id) => !carried.includes(id)));
    } else {
      // Pin it to the branches that carry it now (all of them if none do).
      this.emit(carried.length ? carried : allIds, []);
    }
    this.setState({ notice: "" });
  };

  toggleBranch = (branchId) => {
    const id = String(branchId);
    const on = this.carries(id);

    // An item at no branch at all is what Deactivate is for; an empty pinned
    // list would also mean "every branch", the opposite of what was clicked.
    if (on && this.carriedIds().length === 1) {
      this.setState({
        notice:
          "It has to stay at one branch at least. To take it off sale everywhere, deactivate it instead.",
      });
      return;
    }
    this.setState({ notice: "" });

    if (this.isAllMode()) {
      const excluded = this.excluded();
      if (on) this.emit([], [...excluded, id], this.dropOverride(id));
      else this.emit([], excluded.filter((x) => x !== id));
    } else {
      const pinned = this.pinned();
      if (on) this.emit(pinned.filter((x) => x !== id), [], this.dropOverride(id));
      else this.emit([...pinned, id], []);
    }
  };

  selectAll = () => {
    this.setState({ notice: "" });
    if (this.isAllMode()) this.emit([], []);
    else this.emit(this.state.branches.map((b) => String(b._id)), []);
  };

  getOverride = (branchId) =>
    (this.props.overrides || []).find((o) => String(o.branchId) === String(branchId)) || {};

  hasOverride = (branchId) => {
    const ov = this.getOverride(branchId);
    return (this.props.priceFields || []).some(
      (pf) => ov[pf.key] !== undefined && ov[pf.key] !== null && ov[pf.key] !== ""
    );
  };

  setOverrideField = (branchId, field, value) => {
    const overrides = (this.props.overrides || []).slice();
    const idx = overrides.findIndex((o) => String(o.branchId) === String(branchId));
    if (idx === -1) {
      overrides.push({ branchId, [field]: value });
    } else {
      overrides[idx] = { ...overrides[idx], [field]: value };
    }
    this.emit(this.props.branchIds || [], this.props.excludedBranchIds || [], overrides);
  };

  clearOverride = (branchId) => {
    this.emit(this.props.branchIds || [], this.props.excludedBranchIds || [], this.dropOverride(branchId));
    this.setState((s) => ({ priceOpen: { ...s.priceOpen, [branchId]: false } }));
  };

  // ---------------------------------------------------------------- view

  /** Section caption, matching the console's field captions. */
  caption(text) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 7,
          fontSize: 12,
          fontWeight: 500,
          letterSpacing: ".6px",
          textTransform: "uppercase",
          color: C.taupe,
          marginBottom: 8,
        }}
      >
        <span className="ms" style={{ fontSize: 17 }}>apartment</span>
        {text}
      </div>
    );
  }

  hint(text) {
    return <div style={{ fontSize: 12.5, color: C.taupeLight, marginTop: 8 }}>{text}</div>;
  }

  linkButton(text, onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        style={{
          border: "none",
          background: "none",
          padding: 0,
          color: C.brown,
          fontSize: 13,
          fontWeight: 500,
          cursor: "pointer",
          fontFamily: "inherit",
          textDecoration: "underline",
        }}
      >
        {text}
      </button>
    );
  }

  /** Per-branch price inputs, shared by both the scoped and full views. */
  priceInputs(branchId) {
    const priceFields = this.props.priceFields || [];
    const ov = this.getOverride(branchId);
    if (priceFields.length === 0) return null;
    return (
      <div
        style={{
          display: "grid",
          gridTemplateColumns: `repeat(${Math.min(priceFields.length, 4)}, minmax(0, 1fr))`,
          gap: 10,
          marginTop: 12,
        }}
      >
        {priceFields.map((pf) => (
          <div key={pf.key}>
            <div style={{ fontSize: 11, color: C.taupeLight, marginBottom: 4 }}>{pf.label}</div>
            <input
              type="number"
              placeholder="inherit"
              value={ov[pf.key] != null ? ov[pf.key] : ""}
              onChange={(e) => this.setOverrideField(branchId, pf.key, e.target.value)}
              style={{
                width: "100%",
                height: 36,
                padding: "0 10px",
                border: `1px solid ${C.border}`,
                borderRadius: 9,
                background: "#fff",
                color: C.brownDeep,
                fontSize: 14,
                fontFamily: "inherit",
                outline: "none",
              }}
            />
          </div>
        ))}
      </div>
    );
  }

  /** Price row for a carried branch: collapsed to a link until it is needed. */
  priceRow(branchId) {
    if ((this.props.priceFields || []).length === 0) return null;
    const open = this.state.priceOpen[branchId] || this.hasOverride(branchId);
    if (!open) {
      return (
        <div style={{ marginTop: 8, paddingLeft: 30 }}>
          {this.linkButton("Set a different price here", () =>
            this.setState((s) => ({ priceOpen: { ...s.priceOpen, [branchId]: true } }))
          )}
        </div>
      );
    }
    return (
      <div style={{ paddingLeft: 30 }}>
        {this.priceInputs(branchId)}
        <div style={{ marginTop: 6 }}>
          {this.linkButton("Use the base price", () => this.clearOverride(branchId))}
        </div>
      </div>
    );
  }

  modeCard(all, title, text) {
    const active = this.isAllMode() === all;
    return (
      <div
        onClick={() => this.setMode(all)}
        style={{
          flex: "1 1 200px",
          padding: "10px 12px",
          border: `1px solid ${active ? C.brown : C.tint}`,
          borderRadius: 12,
          background: active ? C.surface : "#fff",
          cursor: "pointer",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span
            style={{
              width: 16,
              height: 16,
              flex: "0 0 16px",
              borderRadius: "50%",
              border: `2px solid ${active ? C.brown : C.border}`,
              display: "grid",
              placeItems: "center",
            }}
          >
            {active && (
              <span style={{ width: 8, height: 8, borderRadius: "50%", background: C.brown }} />
            )}
          </span>
          <span style={{ fontSize: 14, fontWeight: 500, color: C.ink, textTransform: "none" }}>
            {title}
          </span>
        </div>
        <div style={{ fontSize: 12.5, color: C.taupeLight, marginTop: 4, paddingLeft: 24 }}>
          {text}
        </div>
      </div>
    );
  }

  summary() {
    const total = this.state.branches.length;
    const count = this.carriedIds().length;
    if (total > 0 && count === total) {
      return <Chip tone="neutral">At every branch</Chip>;
    }
    return (
      <span style={{ fontSize: 13, color: C.taupe }}>
        At <strong style={{ color: C.brownDeep }}>{count}</strong> of {total} branch
        {total === 1 ? "" : "es"}
      </span>
    );
  }

  // Branch-scoped admins can't reassign branches; the server forces the item
  // onto their own branch. Render a locked single-branch view that only lets
  // them edit the price override for their branch.
  renderBranchScoped() {
    const { branches, loaded } = this.state;
    const priceFields = this.props.priceFields || [];
    const myBranchId = getUserBranchId();
    const myBranch = branches.find((b) => String(b._id) === String(myBranchId));

    return (
      <div className="branch-assignment">
        {this.caption("Your branch")}
        {!loaded && <div style={{ fontSize: 13, color: C.taupeLight }}>Loading branch…</div>}
        {loaded && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              padding: "12px 14px",
              border: `1px solid ${C.tint}`,
              borderRadius: 12,
              background: C.surface,
            }}
          >
            <Chip tone="neutral">{myBranch ? myBranch.name : "Your branch"}</Chip>
            <span style={{ fontSize: 13, color: C.taupe }}>
              Managed for your branch only.
            </span>
          </div>
        )}

        {loaded && myBranchId && this.priceInputs(myBranchId)}
        {priceFields.length > 0 &&
          this.hint("Leave a price blank to inherit the item's base price.")}
      </div>
    );
  }

  render() {
    if (isBranchScopedAdmin()) return this.renderBranchScoped();

    const { branches, loaded, notice } = this.state;
    const allMode = this.isAllMode();

    return (
      <div className="branch-assignment">
        {this.caption("Branch availability")}

        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 12 }}>
          {this.modeCard(true, "All branches", "Untick any branch that shouldn't sell it. New branches get it too.")}
          {this.modeCard(false, "Only the branches I pick", "Tick each branch that sells it. New branches don't.")}
        </div>

        {!loaded && <div style={{ fontSize: 13, color: C.taupeLight }}>Loading branches…</div>}
        {loaded && branches.length === 0 && (
          <div style={{ fontSize: 13, color: C.taupeLight }}>No branches found.</div>
        )}

        {loaded && branches.length > 0 && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 10,
              marginBottom: 8,
            }}
          >
            {this.summary()}
            {this.carriedIds().length < branches.length &&
              this.linkButton(allMode ? "Add back to every branch" : "Tick every branch", this.selectAll)}
          </div>
        )}

        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {branches.map((b) => {
            const id = String(b._id);
            const selected = this.carries(id);
            return (
              <div
                key={id}
                style={{
                  padding: "11px 14px",
                  border: `1px solid ${selected ? C.sand : C.tint}`,
                  borderRadius: 12,
                  background: selected ? C.surface : "#fff",
                }}
              >
                <div
                  onClick={() => this.toggleBranch(id)}
                  style={{ display: "flex", alignItems: "center", gap: 11, cursor: "pointer" }}
                >
                  <span
                    style={{
                      width: 19,
                      height: 19,
                      flex: "0 0 19px",
                      borderRadius: 6,
                      display: "grid",
                      placeItems: "center",
                      background: selected ? C.brown : "#fff",
                      border: selected ? "none" : `1px solid ${C.border}`,
                    }}
                  >
                    {selected && (
                      <span className="ms" style={{ fontSize: 14, color: "#fff" }}>check</span>
                    )}
                  </span>
                  <span style={{ fontSize: 14.5, color: C.ink, textTransform: "none" }}>
                    {b.name}
                  </span>
                  {b.isDefault && <Chip tone="neutral">Default</Chip>}
                  {b.city && (
                    <span style={{ fontSize: 12.5, color: C.taupeLight }}>{b.city}</span>
                  )}
                  {!selected && (
                    <span style={{ marginLeft: "auto", fontSize: 12.5, color: C.taupeLight }}>
                      Not sold here
                    </span>
                  )}
                </div>

                {selected && this.priceRow(id)}
              </div>
            );
          })}
        </div>

        {notice && (
          <div style={{ fontSize: 12.5, color: C.red, marginTop: 8 }}>{notice}</div>
        )}
        {(this.props.priceFields || []).length > 0 &&
          this.hint("A branch without its own price uses the item's base price.")}
      </div>
    );
  }
}

export default BranchAssignment;
